// Unpublished Work © 2023 Deere & Company.

import dayjs from "dayjs";

type DateString = string | null | undefined;

export const formatDate = (
  dateString: DateString | number,
  language: string | undefined,
  convertToUTC: boolean = true,
  convertToLocalTime?: boolean
): string => {
  if (!dateString) {
    return "NA";
  }
  const [locale] = language ? language.split("_") : "";
  const date = new Intl.DateTimeFormat(locale, {
    year: "numeric",
    day: "2-digit",
    month: "short",
    ...(convertToUTC && { timeZone: "UTC" }),
    ...(convertToLocalTime && {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
  }).format(new Date(dateString));
  return date.replace(/de /g, "");
};

export const formatDateWithTime = (
  dateString: DateString | number,
  language: string | undefined,
  convertToUTC: boolean = true,
  convertToLocalTime?: boolean
) => {
  if (!dateString) {
    return "NA";
  }
  const [locale] = language ? language.split("_") : "";
  const date = new Intl.DateTimeFormat(locale, {
    year: "numeric",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    ...(convertToUTC && { timeZone: "UTC" }),
    ...(convertToLocalTime && {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
  }).format(new Date(dateString));
  return date.replace(/de /g, "");
};

export const formatDateAsYYYYMMDD = (
  incomingDate: number | string | null
): string => {
  let dateToConvert = incomingDate;
  if (dateToConvert === null) {
    return "NA";
  }
  if (typeof dateToConvert === "string") {
    dateToConvert = parseInt(dateToConvert, 10);
  }
  const date = new Date(dateToConvert);
  const year = date.getUTCFullYear();
  const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
  const day = date.getUTCDate().toString().padStart(2, "0");

  return `${year}${month}${day}`;
};

export const formatTimestamp = (dateString: DateString | number): string => {
  if (!dateString) {
    return "NA";
  }

  const formatter = new Intl.DateTimeFormat("default", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const date = new Date(dateString);
  const dateParts = formatter.formatToParts(date);
  const getPart = (type: Intl.DateTimeFormatPartTypes) =>
    dateParts.find((part) => part.type === type)?.value;

  const year = getPart("year");
  const month = getPart("month");
  const day = getPart("day");
  const hour = getPart("hour");
  const minute = getPart("minute");
  const second = getPart("second");

  return `${year}-${month}-${day}_${hour}${minute}${second}`;
};

export const formatDateAsLongDate = (dateString: DateString): string => {
  if (!dateString || dateString === "") return "";

  const date = new Date(Date.parse(dateString));
  const formattedDateString = date.toDateString().slice(4).split(" ");
  return `${formattedDateString[1]} ${formattedDateString[0]} ${formattedDateString[2]}`;
};

export const formatNumberInLocale = (
  number: number,
  locale: string,
  fractionDigits?: number
): string =>
  `${number.toLocaleString(locale.replace("_", "-"), {
    maximumFractionDigits: fractionDigits ?? 0,
    minimumFractionDigits: fractionDigits ?? 0,
  })}`;

export const formatCurrencyWithCurrencyCode = (
  number: number,
  currencyCode: string
): string => {
  const formatter = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currencyCode,
  });

  return formatter.format(number);
};

export const formatTime = (
  dateString: DateString | number,
  language: string
): string => {
  if (!dateString) {
    return "NA";
  }

  const [locale] = language.split("_");
  return new Intl.DateTimeFormat(locale, {
    hour: "numeric",
    minute: "numeric",
  })
    .format(new Date(dateString))
    .toLowerCase();
};

export const formatTimeWithLocale = (
  dateString: string | null,
  language: string | undefined
): string => {
  if (dateString === null) {
    return "";
  }
  const [locale] = language ? language.split("_") : "";
  const date = new Intl.DateTimeFormat(locale, {
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  }).format(new Date(dateString));
  return date.replace(/de /g, "");
};

export const convertToCamelCase = (
  str: string,
  separator: string = " "
): string =>
  str
    .split(separator)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(separator);

export const formatDateRange = (
  start: string | null,
  end: string | null,
  language: string
) => {
  const formattedDateRange: string =
    start && end
      ? `${formatDate(start, language)} - ${formatDate(end, language)}`
      : "NA";
  return formattedDateRange;
};

export const currencyFormatter = (
  value: number,
  currency: string,
  language = "en-US"
): string => {
  const formatter = new Intl.NumberFormat(language.replace("_", "-"), {
    style: "currency",
    currency,
  });
  return formatter.format(value).replace(/^(\D+)/, "$1 ");
};

export const currencyFormatterForPromotionV2 = (
  value: number,
  currency: string,
  language = "en-US"
): string => {
  const hasDecimals = value % 1 !== 0;

  const formatter = new Intl.NumberFormat(language.replace("_", "-"), {
    style: "currency",
    currency,
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: hasDecimals ? 2 : 0,
  });
  const formatted = formatter.format(value);

  if (/^\D/.test(formatted)) {
    return formatted.replace(/^([^\d\s]+)\s+(?=\d)/, "$1");
  }

  return formatted;
};

export const formatToLocalDateString = (
  date: Date | undefined
): string | undefined => (date ? dayjs(date).format("YYYY-MM-DD") : undefined);

// Okta User(e.g. names with apostrophes) can arrive HTML-encoded, e.g. "O&#39;Neal"
export const decodeHtmlEntities = (value: string | undefined): string => {
  if (!value) return "";
  return new DOMParser().parseFromString(value, "text/html").documentElement
    .textContent as string;
};
